import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box, IconButton, Paper, Stack, Typography, useTheme } from '@mui/material';
import { MoreVert as MoreVertIcon } from '@mui/icons-material';
import { PieChart } from '@mui/x-charts/PieChart';
import type { WasteTypeWithMaterials } from '../../types/dashboard.type';
import { ESG_BLUE_PALETTE } from '../../constants/esgChartColors';
import { flattenRecycledWasteForChart } from '../../utils/recycledWasteChart.util';
import ChartCard from './ChartCard';

function formatPiePercent(value: number, total: number): string {
  if (total <= 0 || value <= 0) return '';
  const pct = (value / total) * 100;
  if (pct < 0.05) return '<0.1%';
  if (pct < 0.1) return '0.1%';
  if (pct < 1) return `${pct.toFixed(1)}%`;
  if (pct < 10) return `${pct.toFixed(1)}%`;
  return `${pct.toFixed(0)}%`;
}

interface EsgRecycledWasteByTypeChartProps {
  rows: WasteTypeWithMaterials[];
  title: string;
  chartType: 'bar' | 'line' | 'area' | 'pie';
  onMenuClick: (event: React.MouseEvent<HTMLElement>) => void;
}

const EsgRecycledWasteByTypeChart: React.FC<EsgRecycledWasteByTypeChartProps> = ({
  rows,
  title,
  chartType,
  onMenuClick,
}) => {
  const theme = useTheme();
  const flat = useMemo(() => flattenRecycledWasteForChart(rows), [rows]);
  const totalKg = useMemo(() => flat.reduce((s, w) => s + w.total_kg, 0), [flat]);

  const measureRef = useRef<HTMLDivElement>(null);
  const [dims, setDims] = useState({ w: 480, h: 300 });

  useLayoutEffect(() => {
    const el = measureRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width ?? 480;
      const cw = Math.max(280, Math.floor(w));
      setDims({ w: cw, h: Math.max(260, Math.round(cw * 0.58)) });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const pieSeries = useMemo(
    () => [
      {
        type: 'pie' as const,
        data: flat.map((w, i) => ({
          id: i,
          value: w.total_kg,
          label: w.waste_type || 'Unknown',
          color: ESG_BLUE_PALETTE[i % ESG_BLUE_PALETTE.length],
        })),
        innerRadius: '46%',
        outerRadius: '74%',
        paddingAngle: 2,
        cornerRadius: 10,
        startAngle: 0,
        endAngle: 360,
        arcLabel: (item: { value: number }) => formatPiePercent(item.value, totalKg),
        arcLabelMinAngle: 0,
      },
    ],
    [flat, totalKg]
  );

  if (chartType !== 'pie') {
    return (
      <ChartCard
        title={title}
        data={flat}
        type={chartType}
        dataKey="total_kg"
        nameKey="waste_type"
        segmentColors={ESG_BLUE_PALETTE}
        onMenuClick={onMenuClick}
      />
    );
  }

  return (
    <Paper
      sx={{
        p: 3,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '16px',
        bgcolor: theme.palette.background.paper,
        boxShadow: theme.shadows[2],
        transition: 'all 0.3s ease-in-out',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: theme.shadows[4],
        },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
          {title}
        </Typography>
        <IconButton size="small" onClick={onMenuClick} sx={{ color: theme.palette.text.secondary }}>
          <MoreVertIcon />
        </IconButton>
      </Box>

      <Box ref={measureRef} sx={{ width: '100%' }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
          <PieChart
            series={pieSeries}
            width={dims.w}
            height={dims.h}
            margin={{ left: 8, right: 8, top: 8, bottom: 8 }}
            slotProps={{
              legend: { hidden: true },
            }}
          />
        </Box>
      </Box>

      <Box
        sx={{
          mt: 2,
          pt: 2,
          borderTop: 1,
          borderColor: 'divider',
          maxHeight: 220,
          overflowY: 'auto',
        }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          All segments ({flat.length})
        </Typography>
        <Stack spacing={1}>
          {flat.map((w, i) => {
            const color = ESG_BLUE_PALETTE[i % ESG_BLUE_PALETTE.length];
            return (
              <Stack
                key={`${w.waste_type}-${i}`}
                direction="row"
                alignItems="center"
                spacing={1.5}
                sx={{ pr: 1 }}
              >
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: 0.5,
                    bgcolor: color,
                    flexShrink: 0,
                  }}
                />
                <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap title={w.waste_type}>
                  {w.waste_type || 'Unknown'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
                  {w.total_kg.toLocaleString(undefined, { maximumFractionDigits: 2 })} kg
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{ flexShrink: 0, minWidth: 56, textAlign: 'right' }}
                >
                  {formatPiePercent(w.total_kg, totalKg) || '—'}
                </Typography>
              </Stack>
            );
          })}
        </Stack>
      </Box>

    </Paper>
  );
};

export default EsgRecycledWasteByTypeChart;
